/** Toasts: a short message about something the person just did (P13).
 *
 * `showToast("success", "Saved")` puts one at the bottom right. A **success** dismisses itself after
 * 5 seconds; an **error persists** until it is closed, because an error that vanishes is one nobody
 * can act on. A dismissed toast stays in the list for the 140ms exit so it can animate out
 * (`leaving`), then is removed. The list is a small external store read with `useToasts`, rendered by
 * `components/ui/Toast.tsx`'s `ToastRegion`.
 *
 * Toasts never carry a filed figure's value as a changing number, and they say what happened in
 * words: the record of it is the ledger and the screen, not this.
 */

import { useSyncExternalStore } from "react";

export type ToastKind = "success" | "error" | "info";

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  leaving: boolean;
}

/** A success goes by itself after this long. An error never does. */
export const SUCCESS_DISMISS_MS = 5000;
/** The exit animation, `--dur-2` in `tokens.css`. */
export const TOAST_EXIT_MS = 140;

let items: readonly ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(next: readonly ToastItem[]): void {
  items = next;
  listeners.forEach((listener) => listener());
}

export function dismissToast(id: number): void {
  const found = items.find((item) => item.id === id);
  if (!found || found.leaving) return;
  emit(items.map((item) => (item.id === id ? { ...item, leaving: true } : item)));
  window.setTimeout(() => emit(items.filter((item) => item.id !== id)), TOAST_EXIT_MS);
}

export function showToast(kind: ToastKind, message: string): number {
  const id = nextId++;
  emit([...items, { id, kind, message, leaving: false }]);
  if (kind !== "error") window.setTimeout(() => dismissToast(id), SUCCESS_DISMISS_MS);
  return id;
}

/** For tests: forget every toast. */
export function clearToasts(): void {
  emit([]);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useToasts(): readonly ToastItem[] {
  return useSyncExternalStore(
    subscribe,
    () => items,
    () => items,
  );
}
